import React, { useMemo } from 'react';

import { type Fetcher, PlacementFetchingProvider } from '@atlassian/post-office-placement-data';
// TODO: Export these shared contract types from a remote package so they can be imported here
// import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context';

import {
	type EligibleMessageLikeType,
	type Payload,
	type PlacementPreviewProviderProps,
	type ReturnOverride,
} from './types';

export const PLACEMENT_REQUEST_URL = `/gateway/api/post-office/api/v1/placements/`;
export const PREVIEW_REQUEST_URL = `/gateway/api/post-office/api/v1/placement-preview/`;

export const PlacementPreviewProvider = <
	EligibleMessageType extends EligibleMessageLikeType = EligibleMessageLikeType,
>({
	returnOverride,
	eligibleMessages,
	children,
}: PlacementPreviewProviderProps<EligibleMessageType>): React.JSX.Element => {
	const fetch = useMemo(() => {
		if (returnOverride) {
			return createReturnOverrideFetch({ returnOverride });
		}

		return createPreviewFetch({ eligibleMessages });
	}, [eligibleMessages, returnOverride]);

	return <PlacementFetchingProvider fetch={fetch}>{children}</PlacementFetchingProvider>;
};

export const createReturnOverrideFetch =
	({ returnOverride }: { returnOverride: ReturnOverride }): Fetcher['fetch'] =>
	async () => {
		return new Response(JSON.stringify(returnOverride), {
			status: 200,
			statusText: 'ok',
		});
	};

export const createPreviewFetch =
	<EligibleMessageType extends EligibleMessageLikeType = EligibleMessageLikeType>({
		eligibleMessages,
	}: {
		eligibleMessages: Array<EligibleMessageType>;
	}): Fetcher['fetch'] =>
	(url, options) => {
		const finalUrl = url.replace(PLACEMENT_REQUEST_URL, PREVIEW_REQUEST_URL);

		const payload: Payload<EligibleMessageType> = {
			originalMethod: options?.method || 'GET',
			originalBody: parseBody(options?.body),
			eligibleMessages,
		};

		return globalThis.fetch(finalUrl, {
			...options,
			method: 'POST',
			headers: {
				...(options?.headers || {}),
			},
			body: stringifyPayload(payload),
		});
	};

const objectHasKeys = (input: NonNullable<unknown>): input is Record<string, unknown> =>
	!!Object.keys(input).length;

const parseBody = (body?: BodyInit | null): Record<string, unknown> | undefined => {
	if (typeof body === 'undefined' || body === null) {
		return undefined;
	}

	try {
		const parsedBody = JSON.parse(body as string) as NonNullable<unknown>;

		if (objectHasKeys(parsedBody)) {
			return parsedBody;
		}

		throw new Error();
	} catch {
		throw new Error('Unable to parse preview body. Is it a valid object?');
	}
};

const stringifyPayload = (payload: Payload): string => {
	try {
		return JSON.stringify(payload);
	} catch {
		throw new Error('Unable to parse preview body. Is it a valid object?');
	}
};
